import { create } from "zustand";
import axios from "axios";
import { LOG } from "../utils/logUtil";
import { useAppLoading } from "./useAppLoading";
import { IUser } from "./useUsers";
import { IPartner } from "./usePartners";

export interface IParcelHandoff {
  _id: string;
  user: IUser | string;
  carrierPartner: IPartner | string;
  location: { _id: string; name?: string; city?: string } | string;
  registeredBy: IUser | string;
  /** BOTH when one registration has pickups and drop-offs. */
  direction: "PICKUP" | "DROPOFF" | "BOTH";
  /** Total of both sides. */
  parcelCount: number;
  /** Absent on rows saved before the split; read them via parcelSplit. */
  pickupCount?: number;
  dropoffCount?: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface IParcelHandoffFilters {
  search?: string;
  carrierPartnerId?: string;
  direction?: "PICKUP" | "DROPOFF" | "";
  locationId?: string;
  dateFrom?: string;
  dateTo?: string;
}

interface IStore {
  /** State */
  parcelHandoffs: IParcelHandoff[];
  hasNextPage: boolean;
  isLoading: boolean;
  error: string | null;
  /** Callbacks */
  getParcelHandoffs: (
    page: number,
    limit: number,
    filters?: IParcelHandoffFilters
  ) => void;
  clearParcelHandoffs: () => void;
}

/** Incremented per request; only the newest is allowed to write to state. */
let requestSequence = 0;

/**
 * Appending a page can repeat a row: a handoff registered between two
 * requests shifts the offset, so the last row of page 1 reappears on page 2.
 * Keyed by _id, first occurrence wins, so the order stays as the server sent
 * it.
 */
const dedupeById = (items: IParcelHandoff[]): IParcelHandoff[] => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const id = String(item?._id);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

export const useParcelHandoffs = create<IStore>()((set) => ({
  /** State */
  parcelHandoffs: [],
  hasNextPage: false,
  isLoading: false,
  error: null,
  /** Callbacks */
  getParcelHandoffs: async (page, limit, filters = {}) => {
    // Monotonic sequence, checked before anything is written to state. Filter
    // changes fire requests faster than they come back, and without this an
    // earlier, slower response lands last and replaces the list the operator
    // is actually looking at with results for a filter they already changed.
    const sequence = ++requestSequence;
    const isStale = () => sequence !== requestSequence;

    useAppLoading.getState().setIsLoading(true);
    set({ isLoading: true, error: null });
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      // Empty filters are left out entirely rather than sent as blanks — the
      // backend treats a present-but-empty value as a filter to apply.
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, String(value));
      });

      const response = await axios.get(`parcel-handoffs?${params.toString()}`);
      if (isStale()) return;

      const items: IParcelHandoff[] = response?.data?.items ?? [];
      set((state) => ({
        ...state,
        hasNextPage: Boolean(response?.data?.hasNextPage),
        parcelHandoffs:
          page === 1 ? items : dedupeById([...state.parcelHandoffs, ...items]),
      }));
    } catch (error: any) {
      if (isStale()) return;
      LOG.error("Error on fetching parcel handoffs", { error });
      set({
        error:
          error?.response?.data?.message ??
          "Could not load parcel handoffs. Please try again.",
      });
    } finally {
      if (!isStale()) {
        set({ isLoading: false });
      }
      useAppLoading.getState().setIsLoading(false);
    }
  },
  clearParcelHandoffs: () => {
    // Bumping the sequence cancels any in-flight response, so a request from
    // the screen we just left cannot repopulate the list behind us.
    requestSequence += 1;
    set({ parcelHandoffs: [], hasNextPage: false, error: null });
  },
}));
